import * as React from 'react';
import { useMsgText } from './hooks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import Spacer from '../layout/Spacer';
import LocalDiv from './LocalDiv';
import { FLEX_ROW, FLEX_COLUMN } from '../layout/flexBox';
import { css, SerializedStyles } from '@emotion/core';
import Msg from './Msg';


const styles = {
  content:            [
    css`
      align-items: center;
      justify-content: center;
      flex-wrap: nowrap;
    `,
  ],
  label:              css`
    white-space: nowrap;
  `,
  mobileToolbarLabel: css`
    overflow-wrap: break-spaces;
    text-transform: uppercase;
  `,
};


interface Props extends React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement> {
  /**
   * The translation key to lookup for the button text
   */
  str: string | number;

  /**
   * Fallback text to be used for the translated button text can't be found
   */
  defaultText?: string;

  /**
   * Optional FontAwesome icon; renders left of the button text in LTR, right in RTL
   */
  icon?: IconDefinition;

  /**
   * Optional styling for the font awesome icon
   */
  iconStyle?: SerializedStyles;

  /**
   * If true, sets custom styling used for mobile toolbar buttons (icon above label, upper-cased label, etc.)
   */
  useMobileToolbarStyling?: boolean;

  /**
   * Optional SVG image; renders left of the button text in LTR, right in RTL
   */
  svg?: React.ReactNode;
}


/**
 * LocalBtn: a button component that accepts all the normal button props and renders the button's inner text in the
 * layout direction given by the Text Direction in Redux (taken from the user's locale).
 *
 * Props:
 * 1. The `str` prop is your translation key that is used to look up the appropriate translation for the button
 * text in Redux.
 * 1. The `defaultText` prop allows you to specify button text for when a translation isn't found (defaults to empty
 * string). If this prop is not defined, a default message will be used (if one has been defined).
 *
 * PureComponent: true
 * Redux Connected: true
 */
const LocalBtn: React.FC<Props> = (props) => {
  // Destructure to appease React
  const { str, defaultText, icon, svg, useMobileToolbarStyling, ...rest } = props;

  const translation = useMsgText(str, defaultText);

  return (
    <button {...rest}>
      <LocalDiv css={[styles.content, useMobileToolbarStyling ? FLEX_COLUMN : FLEX_ROW]}>
        {
          !!icon && (
            <>
              <FontAwesomeIcon icon={icon!} size={'lg'} css={props.iconStyle} />
              <Spacer sizePx={8} />
            </>
          )
        }
        {
          !!svg && (
            <>
              {svg}
              <Spacer sizePx={8} />
            </>
          )}
        <Msg
          str=''
          defaultMsg={translation}
          css={[useMobileToolbarStyling ? styles.mobileToolbarLabel : styles.label]}
        />
      </LocalDiv>
    </button>
  );
};

LocalBtn.defaultProps = {
  defaultText:             '',
  useMobileToolbarStyling: false,
};

export default LocalBtn;
